<template>
	<div class="d-hearing-list-custom">
		<ListPageLayout
			v-if="true"
			ref="pageLayout"
			class="d-hearing-list-custom"
			v-bind="{ ...$attrs, ...$props }"
			:search-config="{
				limit: {
					initial: showSupportTile ? 11 : 12,
					value: 12,
				},
				searchResponseTransformerMethod,
			}"
		>
			<template #filters="{ filters, filterValues, updateValue }">
				<div v-if="filters" class="mt-3xl">
					<div
						v-if="filters.status"
						class="flex flex-wrap gap-x-layout-gutter gap-y-sm"
					>
						<BaseInputRadioButtons
							id="radio-status"
							class="w-full mb-32"
							v-bind="filters.status"
							:value="filterValues.status"
							:options="filters.status.value"
							@input="(value) => updateValue('status', value)"
						/>
					</div>

					<!-- topics, areas, text -->
					<BaseInputSearch
						v-if="filters.text"
						v-model="textValue"
						class="
							>=1200:w-8/12col
							>=1024:w-12/12col
							w-full
							text-input
							mb-sm
							>=1024:mb-layout-gutter
						"
						v-bind="filters.text"
						@search="filters.text.value = $event"
					/>

					<div
						v-if="filters.topics || filters.areas"
						class="flex flex-wrap gap-x-layout-gutter gap-y-sm"
					>
						<BaseInputDropdown
							v-if="filters.topics"
							id="dropdown-topics"
							class="
								>=1200:w-5/12col
								>=1024:w-8/12col
								w-full
								z-10
							"
							v-bind="filters.topics"
							:value="filterValues.topics"
							:options="filters.topics.value"
							@input="(value) => updateValue('topics', value)"
						/>

						<BaseInputDropdown
							v-if="filters.areas"
							id="dropdown-areas"
							class="
								>=1200:w-3/12col
								>=1024:w-4/12col
								w-full
								z-10
							"
							v-bind="filters.areas"
							:value="filterValues.areas"
							:options="filters.areas.value"
							@input="(value) => updateValue('areas', value)"
						/>
					</div>
				</div>
			</template>

			<template #results="{ data }">
				<div
					:class="[
						'grid gap-layout-gutter',
						'grid-cols-1 >=768:grid-cols-2 >=1024:grid-cols-3',
					]"
				>
					<template v-for="item in data">
						<!-- Support Tile -->
						<div
							v-if="item.isSupportTile"
							:key="`item-${item.id}`"
							class="
								bg-secondary
								px-xl
								py-lg
								flex flex-col
								items-start
							"
						>
							<h2
								v-if="item.title"
								:class="[
									'font-darker-grotesque font-bold',
									'text-title-card ',
								]"
								v-text="item.title"
							></h2>

							<p
								v-if="item.text"
								class="mt-md text-teaser-sm"
								v-text="item.text"
							></p>

							<NuxtLinkExt
								v-if="item.link"
								:to="item.link.url"
								:target="item.link.target"
								class="mt-auto"
							>
								<BaseButton
									tag="div"
									light
									class="inline-block mt-lg"
									inner-class="bg-transparent"
								>
									{{ item.link.name }}

									<template #icon>
										<SvgIconArrow
											:class="
												isExternal(item.link.url) &&
												'transform -rotate-45'
											"
										/>
									</template>
								</BaseButton>
							</NuxtLinkExt>
						</div>

						<ContentCard
							v-else
							:key="`item-${item.id}`"
							v-bind="{
								title: item.title || item.name,
								link: { url: item.url, target: item.target },
								image: item.image,
							}"
						>
							<template #before>
								<div
									:class="[
										'font-darker-grotesque font-semibold text-label-card',
										'mb-6',
									]"
								>
									<span
										v-text="
											item.type === 'HearingPage'
												? 'Høringsfrist'
												: 'Klagefrist'
										"
									></span>

									<span
										v-text="formatDate(item.deadline)"
									></span>
								</div>
							</template>

							<template #content>
								<div
									class="grid gap-12 text-teaser-sm"
									style="grid-template-columns: auto 1fr"
								>
									<span
										class="font-bold"
										v-text="'Kategori'"
									></span>
									<span
										v-if="item.topic"
										v-text="item.topic.name"
									></span>

									<span
										class="font-bold"
										v-text="'Område'"
									></span>
									<span
										v-if="item.area"
										v-text="item.area.name"
									></span>

									<span
										class="font-bold"
										v-text="'Offentliggjort'"
									></span>
									<span
										v-if="item.published"
										v-text="formatDate(item.published)"
									></span>
								</div>
							</template>
						</ContentCard>
					</template>
				</div>
			</template>
		</ListPageLayout>

		<FeedbackModule
			v-if="!hideFeedback"
			:feedback="feedback"
			class="mt-7xl"
		/>
	</div>
</template>

<script>
import ListPageLayout from '~/components/shared/ListPageLayout';
import ContentCard from '~/components/shared/ContentCard';
import FeedbackModule from '~/components/shared/FeedbackModule';

export default {
	name: 'MainHearingListCustom',

	components: {
		ListPageLayout,
		ContentCard,
		FeedbackModule,
	},

	inheritAttrs: false,

	props: {
		searchFilters: Object,
		searchConfig: {
			type: Object,
			default: () => ({}),
		},
		supportTile: {
			type: Object,
			default: null,
		},
		hideFeedback: {
			type: Boolean,
			default: false,
		},
		feedback: Object,
	},

	data() {
		return {
			textValue:
				this.searchFilters?.fields?.find((e) => e.name === 'text')
					.value || '',
		};
	},

	computed: {
		showSupportTile() {
			return this.supportTile !== null;
		},
	},

	methods: {
		isExternal(url) {
			if (url) {
				if (
					['http://', 'https://', 'ftp://'].some(
						(str) => url.indexOf(str) === 0
					)
				) {
					return true;
				}

				const splitOut = url.split('/');
				if (splitOut[0].indexOf('.') >= 0) {
					return true;
				}
			}

			return false;
		},
		formatDate(dateStr) {
			const date = new Date(dateStr);
			date.setHours(date.getHours() - 2);
			const day = `0${date.getDate()}`.slice(-2);
			const month = `0${date.getMonth() + 1}`.slice(-2);
			const year = date.getFullYear();
			return `${day}.${month}.${year}`;
		},
		searchResponseTransformerMethod(res) {
			// Insert job agent
			if (this.showSupportTile && res?.data?.pagination?.offset === 0) {
				res.data.data = res.data.data || [];
				res.data.data.splice(2, 0, {
					isSupportTile: true,
					...this.supportTile,
				});
			}
			return res;
		},
	},
};
</script>
